import { useEffect, useRef } from 'react';
import {
  subscribeScrollProgress,
  pushAnchors,
  smoothstep01,
  mapRange,
  type AnchorLabel,
} from '../heroSignals';

// Local-space anchor points (relative to model center after centering).
// `facingYaw` is the yaw angle (in radians) around Y where this anchor
// is best facing the camera. We use it to gate label visibility.
type AnchorDef = {
  id: string;
  label: string;
  value: string;
  side: 'left' | 'right';
  nx: number;
  ny: number;
  nz: number;
  facingYaw: number; // radians; the model.rotation.y where anchor faces camera
};

// We plan a rotation range of ~130deg (2.27 rad) during the label stage.
// Space each anchor's "sweet spot" evenly across that range so at any time
// no more than ~2 anchors are near-facing simultaneously.
// Anchor local positions are placed at direction (-sin(a), 0, cos(a)) * r
// so that when model.rotation.y === facingYaw, the anchor faces the camera.
// facingYaw values are spread across the 0 -> 240deg scroll rotation range.
const ANCHOR_DEFS: AnchorDef[] = [
  {
    id: 'driver',
    label: 'COMPONENT · DRIVER',
    value: '40mm Beryllium',
    side: 'left',
    nx: -0.192, // -sin(0.5) * 0.4
    ny: 0.15,
    nz: 0.351, //  cos(0.5) * 0.4
    facingYaw: 0.5,
  },
  {
    id: 'play',
    label: 'CONTROL · PLAY',
    value: 'Tactile Micro-switch',
    side: 'right',
    nx: -0.4, // -sin(1.6) * 0.4
    ny: 0.08,
    nz: -0.012, //  cos(1.6) * 0.4
    facingYaw: 1.6,
  },
  {
    id: 'cell',
    label: 'MODULE · CELL',
    value: '5000mAh Lithium',
    side: 'left',
    nx: -0.175, // -sin(2.7) * 0.4
    ny: -0.18,
    nz: -0.362, //  cos(2.7) * 0.4
    facingYaw: 2.7,
  },
  {
    id: 'antenna',
    label: 'ARRAY · ANTENNA',
    value: 'Wideband RF',
    side: 'right',
    nx: 0.246, // -sin(3.8) * 0.4
    ny: 0.28,
    nz: -0.316, //  cos(3.8) * 0.4
    facingYaw: 3.8,
  },
];

export default function ProductShowcase() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let disposed = false;
    let animationId = 0;
    let renderer: any;
    let scene: any;
    let camera: any;
    let model: any;
    let modelGroup: any;
    let handleResize: (() => void) | null = null;
    let handlePointerMove: ((e: PointerEvent) => void) | null = null;
    let handlePointerLeave: (() => void) | null = null;
    let unsubScroll: (() => void) | null = null;

    const target = { x: 0, y: 0 };
    const current = { x: 0, y: 0 };
    const scrollState = { p: 0 };

    (async () => {
      try {
        const threeUrl = 'https://unpkg.com/three@0.160.0/build/three.module.js';
        const gltfUrl = 'https://unpkg.com/three@0.160.0/examples/jsm/loaders/GLTFLoader.js';
        const THREE: any = await import(/* @vite-ignore */ threeUrl);
        const { GLTFLoader }: any = await import(/* @vite-ignore */ gltfUrl);

        if (disposed) return;

        const width = container.clientWidth;
        const height = container.clientHeight;

        scene = new THREE.Scene();
        camera = new THREE.PerspectiveCamera(48, width / height, 0.01, 100);
        camera.position.set(0, 0, 1);

        renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.setSize(width, height);
        renderer.setClearColor(0x000000, 0);
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.1;
        container.appendChild(renderer.domElement);

        const ambient = new THREE.AmbientLight(0xffffff, 0.35);
        scene.add(ambient);
        const keyLight = new THREE.DirectionalLight(0xffffff, 1.6);
        keyLight.position.set(-1.2, 1.4, 1.2);
        scene.add(keyLight);
        const fillLight = new THREE.DirectionalLight(0xa8b8d8, 0.5);
        fillLight.position.set(1.5, 0.4, -1.2);
        scene.add(fillLight);
        const rimLight = new THREE.DirectionalLight(0xffffff, 0.35);
        rimLight.position.set(0, -0.6, -1.5);
        scene.add(rimLight);

        const loader = new GLTFLoader();
        const primaryUrl =
          'https://raw.githubusercontent.com/KhronosGroup/glTF-Sample-Models/main/2.0/BoomBox/glTF-Binary/BoomBox.glb';
        const fallbackUrl =
          'https://cdn.jsdelivr.net/gh/KhronosGroup/glTF-Sample-Models@main/2.0/BoomBox/glTF-Binary/BoomBox.glb';

        const loadModel = (url: string) =>
          new Promise<any>((resolve, reject) => {
            loader.load(url, resolve, undefined, reject);
          });

        let gltf: any;
        try {
          gltf = await loadModel(primaryUrl);
        } catch (err) {
          console.warn('Primary GLB failed, trying jsDelivr fallback', err);
          gltf = await loadModel(fallbackUrl);
        }

        if (disposed) return;

        model = gltf.scene;

        // ----- Chalk -> color reveal shader (uses LOCAL position so the reveal
        // pattern stays "painted on" the mesh as the model rotates) -----
        const shaderUniforms: any[] = [];
        const materials: any[] = [];

        model.traverse((child: any) => {
          if (!child.isMesh) return;
          const oldMat = child.material;
          const baseMap = oldMat?.map || null;
          const normalMap = oldMat?.normalMap || null;

          const newMat = new THREE.MeshStandardMaterial({
            color: new THREE.Color('#ffffff'),
            map: baseMap,
            normalMap,
            roughness: 0.55,
            metalness: 0.2,
            transparent: true,
            opacity: 0,
          });

          const uColorProgress = { value: 0 };
          const uChalk = { value: new THREE.Color('#C8D2E0') };
          const uSoft = { value: 0.22 };
          // Colorway variant — tints the LOWER portion of the model with a
          // brand color ("Slate Green") so the AX-07 shown in Chapter 002
          // reads as a distinct colorway rather than the black/white ref unit.
          const uColorwayColor = { value: new THREE.Color('#5A8A78') };
          const uColorwayThresholdY = { value: 0.0 };
          const uColorwaySoftY = { value: 0.05 };
          const uColorwayStrength = { value: 0.0 };

          newMat.onBeforeCompile = (shader: any) => {
            shader.uniforms.uColorProgress = uColorProgress;
            shader.uniforms.uChalk = uChalk;
            shader.uniforms.uSoft = uSoft;
            shader.uniforms.uColorwayColor = uColorwayColor;
            shader.uniforms.uColorwayThresholdY = uColorwayThresholdY;
            shader.uniforms.uColorwaySoftY = uColorwaySoftY;
            shader.uniforms.uColorwayStrength = uColorwayStrength;

            shader.vertexShader = shader.vertexShader
              .replace(
                '#include <common>',
                `#include <common>\nvarying vec3 vLocalPosReveal;`
              )
              .replace(
                '#include <begin_vertex>',
                `#include <begin_vertex>\nvLocalPosReveal = position;`
              );

            shader.fragmentShader = shader.fragmentShader
              .replace(
                '#include <common>',
                `#include <common>
                 varying vec3 vLocalPosReveal;
                 uniform float uColorProgress;
                 uniform vec3 uChalk;
                 uniform float uSoft;
                 uniform vec3 uColorwayColor;
                 uniform float uColorwayThresholdY;
                 uniform float uColorwaySoftY;
                 uniform float uColorwayStrength;
                 float revealHash(vec3 p){
                   return fract(sin(dot(p, vec3(17.31, 43.19, 91.77))) * 43758.5453);
                 }
                 float revealNoise(vec3 p){
                   vec3 i = floor(p);
                   vec3 f = fract(p);
                   f = f*f*(3.0-2.0*f);
                   float n000 = revealHash(i + vec3(0,0,0));
                   float n100 = revealHash(i + vec3(1,0,0));
                   float n010 = revealHash(i + vec3(0,1,0));
                   float n110 = revealHash(i + vec3(1,1,0));
                   float n001 = revealHash(i + vec3(0,0,1));
                   float n101 = revealHash(i + vec3(1,0,1));
                   float n011 = revealHash(i + vec3(0,1,1));
                   float n111 = revealHash(i + vec3(1,1,1));
                   float nx00 = mix(n000, n100, f.x);
                   float nx10 = mix(n010, n110, f.x);
                   float nx01 = mix(n001, n101, f.x);
                   float nx11 = mix(n011, n111, f.x);
                   float nxy0 = mix(nx00, nx10, f.y);
                   float nxy1 = mix(nx01, nx11, f.y);
                   return mix(nxy0, nxy1, f.z);
                 }`
              )
              .replace(
                '#include <color_fragment>',
                `#include <color_fragment>
                 float _threshold = revealNoise(vLocalPosReveal * 220.0);
                 float _edge = smoothstep(_threshold - uSoft, _threshold + uSoft, uColorProgress);
                 vec3 _revealedColor = diffuseColor.rgb;
                 // Colorway remap: tint the LOWER half using the brand color.
                 // Preserve luminance of the original so black stays dark, white
                 // gets tinted — the top half stays completely untouched.
                 float _bottomMask = 1.0 - smoothstep(uColorwayThresholdY - uColorwaySoftY, uColorwayThresholdY + uColorwaySoftY, vLocalPosReveal.y);
                 float _lum = dot(_revealedColor, vec3(0.299, 0.587, 0.114));
                 vec3 _tinted = mix(_revealedColor * 0.6, uColorwayColor * (0.35 + _lum * 0.9), 0.75);
                 _revealedColor = mix(_revealedColor, _tinted, _bottomMask * uColorwayStrength);
                 diffuseColor.rgb = mix(uChalk, _revealedColor, _edge);`
              );
          };

          child.material = newMat;
          shaderUniforms.push({
            uColorProgress,
            uChalk,
            uSoft,
            uColorwayColor,
            uColorwayThresholdY,
            uColorwaySoftY,
            uColorwayStrength,
          });
          materials.push(newMat);
        });

        // Center + scale
        const box = new THREE.Box3().setFromObject(model);
        const size = new THREE.Vector3();
        const center = new THREE.Vector3();
        box.getSize(size);
        box.getCenter(center);
        model.position.sub(center);

        // Now that we know the mesh's local Y range, calibrate the colorway
        // threshold so the tint applies to roughly the bottom half of the
        // model (in the mesh's original vertex space, which is what the
        // shader receives via `vLocalPosReveal`).
        const colorwayThresholdY = center.y - size.y * 0.02; // slightly above true middle
        const colorwaySoftY = size.y * 0.08; // 8% of height — gentle blend
        for (const u of shaderUniforms) {
          u.uColorwayThresholdY.value = colorwayThresholdY;
          u.uColorwaySoftY.value = colorwaySoftY;
        }

        const maxDim = Math.max(size.x, size.y, size.z);
        const fov = camera.fov * (Math.PI / 180);
        const viewHeightAtOrigin = 2 * Math.tan(fov / 2) * camera.position.z;
        const targetHeight = viewHeightAtOrigin * 0.46;
        const finalScale = targetHeight / maxDim;

        modelGroup = new THREE.Group();
        modelGroup.add(model);
        modelGroup.scale.setScalar(0.001);
        scene.add(modelGroup);

        const anchorObjects: {
          def: AnchorDef;
          obj: any;
        }[] = ANCHOR_DEFS.map((def) => {
          const local = new THREE.Vector3(
            def.nx * size.x,
            def.ny * size.y,
            def.nz * size.z
          );
          const obj = new THREE.Object3D();
          obj.position.copy(local);
          model.add(obj);
          return { def, obj };
        });

        handlePointerMove = (e: PointerEvent) => {
          const rect = container.getBoundingClientRect();
          const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
          const ny = ((e.clientY - rect.top) / rect.height) * 2 - 1;
          target.x = Math.max(-1, Math.min(1, nx));
          target.y = Math.max(-1, Math.min(1, ny));
        };
        handlePointerLeave = () => {
          target.x = 0;
          target.y = 0;
        };
        container.addEventListener('pointermove', handlePointerMove);
        container.addEventListener('pointerleave', handlePointerLeave);

        handleResize = () => {
          if (!container || !renderer || !camera) return;
          const w = container.clientWidth;
          const h = container.clientHeight;
          renderer.setSize(w, h);
          camera.aspect = w / h;
          camera.updateProjectionMatrix();
        };
        window.addEventListener('resize', handleResize);

        unsubScroll = subscribeScrollProgress((p) => {
          scrollState.p = p;
        });

        // Rotation plan (progress = scrollY / (docHeight - viewport), doc = 500vh):
        //   - Intro: model spins in from -1.2π -> 0 (facing camera)
        //   - 0.04 -> 0.20  gentle chalk-stage scroll rotation (28°)
        //   - 0.20 -> 0.45  COLOR REVEAL: model stays perfectly still
        //   - 0.45 -> 0.75  labels stage: scroll rotates model 240°
        //   - 0.75 -> 0.95  Chapter 002: model shrinks, moves to left,
        //                    self-rotation slows and stops
        const introDurationMs = 1400;
        const startTime = performance.now();
        const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
        const easeOutBack = (t: number) => {
          const c1 = 1.70158;
          const c3 = c1 + 1;
          return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
        };
        // power2.inOut equivalent for the chapter-2 transform scrub.
        const easeInOutQuad = (t: number) =>
          t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

        model.rotation.y = -Math.PI * 1.2;

        const baseTiltX = 0.14;
        const baseTiltY = 0.18;
        const stage2ExtraTiltX = 0.12;
        const stage2ExtraTiltY = 0.16;

        // Chalk-stage rotation: while the WHITE model is on screen (before
        // the color reveal begins), scrolling gently rotates it so it never
        // feels static. IMPORTANT: this ends BEFORE the color reveal starts
        // (0.20), so during the color reveal the model stays still.
        const CHALK_ROT_START = 0.04;
        const CHALK_ROT_END = 0.20;
        const CHALK_ROT_TOTAL = (28 * Math.PI) / 180; // ~0.49 rad

        // Label stage rotation kicks in after the color reveal ends.
        const SCROLL_ROT_START = 0.45;
        const SCROLL_ROT_END = 0.75;
        const SCROLL_ROT_TOTAL = (240 * Math.PI) / 180; // ~4.19 rad

        // Chapter 002 transform window — model migrates to the left card.
        // Wider window so the shrink + horizontal move reads as an obviously
        // continuous scrub, not a snap-in. Halfway through this window the
        // model is at roughly half-scale, half-position.
        const CH2_START = 0.62;
        const CH2_END = 0.90;

        const projected = new THREE.Vector3();

        const animate = () => {
          if (disposed) return;
          const now = performance.now();
          const elapsed = now - startTime;
          const introT = Math.min(1, elapsed / introDurationMs);

          // Chapter-2 transform progress (0 → 1 across 0.75 → 0.95).
          const ch2Raw = mapRange(scrollState.p, CH2_START, CH2_END);
          const ch2T = easeInOutQuad(ch2Raw); // power2.inOut

          // Stage-1 grow (during headline fade) is now shifted earlier.
          const s1 = mapRange(scrollState.p, 0.10, 0.20);

          const introScale = easeOutBack(introT) * finalScale;
          const stageScale = 1 + s1 * 0.25;
          // Chapter-2 sizing: model shrinks continuously to ~80% so the
          // scale change is still visible during scroll, but the final
          // resting size fills the card generously with minimal empty space.
          const ch2Scale = 1 - ch2T * 0.20;
          modelGroup.scale.setScalar(
            Math.max(0.0001, introScale * stageScale * ch2Scale)
          );

          // Chapter-2 move-left: 0vw -> -28vw (screen x). Convert to world
          // units at the camera's z plane so it matches the pixel target.
          const viewWidthAtOrigin =
            viewHeightAtOrigin * (camera.aspect || 1);
          const targetLeftOffsetVW = -0.28; // -28% of viewport width
          // Extra rightward nudge (in world units) so the model matches the
          // card's +60px shift toward the viewport center. Scaled by ch2T so
          // it only kicks in as chapter 2 progresses.
          const containerWidthPx = container.clientWidth || 1;
          const extraRightWorld =
            (60 / containerWidthPx) * viewWidthAtOrigin;
          modelGroup.position.x =
            ch2T * (targetLeftOffsetVW * viewWidthAtOrigin + extraRightWorld);
          modelGroup.position.y = 0;

          const op = easeOutCubic(introT);
          for (const m of materials) m.opacity = op;

          // Colorway tint intentionally disabled — model keeps its original
          // material appearance across chapter 2.
          for (const u of shaderUniforms) {
            u.uColorwayStrength.value = 0;
          }

          // Color reveal window — 0.20 → 0.45. Model is completely still.
          const s2Raw = mapRange(scrollState.p, 0.20, 0.45);
          const s2Eased =
            s2Raw < 0.5
              ? 2 * s2Raw * s2Raw
              : 1 - Math.pow(-2 * s2Raw + 2, 2) / 2;
          const s2 = s2Eased;
          for (const u of shaderUniforms) u.uColorProgress.value = s2;

          // ---- Rotation driven by scroll (scrub) ----
          // Intro rotation lerp from -1.2π to 0 during introT.
          const introRotation = -Math.PI * 1.2 * (1 - easeOutCubic(introT));
          const chalkRotT = mapRange(
            scrollState.p,
            CHALK_ROT_START,
            CHALK_ROT_END
          );
          const chalkRotation = chalkRotT * CHALK_ROT_TOTAL;
          // Passive idle rotation — chalk model slowly turns on its own
          // BEFORE the color reveal begins. Attenuates quickly as we approach
          // the coloring window (0.20) so the model is completely still while
          // colors are being revealed. In chapter 2 it also stays off.
          const idleSeconds = Math.max(0, elapsed - introDurationMs) / 1000;
          const idleAttenuation = 1 - mapRange(scrollState.p, 0.14, 0.20);
          const idleRotation =
            idleSeconds * 0.14 * introT * idleAttenuation; // ~8deg/sec at idle
          const scrollRotT = mapRange(
            scrollState.p,
            SCROLL_ROT_START,
            SCROLL_ROT_END
          );
          const scrollRotation = scrollRotT * SCROLL_ROT_TOTAL;
          // Chapter-2 continuous self-rotation — the model keeps spinning
          // inside the blue reference card the whole time. Ramps up as we
          // enter chapter 2 and then stays at full speed.
          const ch2DriftRate = 0.55; // rad/sec (~31°/s) at peak — clearly visible
          const ch2DriftBoost = mapRange(scrollState.p, 0.66, 0.80);
          const ch2Drift = idleSeconds * ch2DriftRate * ch2DriftBoost;
          model.rotation.y =
            introRotation +
            idleRotation +
            chalkRotation +
            scrollRotation +
            ch2Drift;

          // Smooth pointer follow (lerp)
          current.x += (target.x - current.x) * 0.08;
          current.y += (target.y - current.y) * 0.08;

          // Pointer tilt gets stronger during stage 2 (~15deg), but tapers off
          // as chapter 2 progresses (model is a "supporting actor" then).
          const tiltDamp = 1 - ch2T * 0.7;
          const tiltX = (baseTiltX + stage2ExtraTiltX * s2) * tiltDamp;
          const tiltY = (baseTiltY + stage2ExtraTiltY * s2) * tiltDamp;
          modelGroup.rotation.y = current.x * tiltY;
          modelGroup.rotation.x = current.y * tiltX;

          renderer.render(scene, camera);

          // ---- Anchors ----
          // Only broadcast anchors during the labels stage. Also stop once
          // chapter 2 kicks in so dots don't linger over the shrunk model.
          if (scrollState.p >= 0.44 && scrollState.p < 0.68) {
            const rect = container.getBoundingClientRect();
            const w = rect.width;
            const h = rect.height;
            const currentYaw = model.rotation.y; // includes both intro and scroll
            const out: AnchorLabel[] = anchorObjects.map(({ def, obj }) => {
              obj.getWorldPosition(projected);
              projected.project(camera);
              const sx = (projected.x * 0.5 + 0.5) * w;
              const sy = (-projected.y * 0.5 + 0.5) * h;
              const inFrustum =
                projected.z < 1 &&
                sx > -20 &&
                sx < w + 20 &&
                sy > -20 &&
                sy < h + 20;
              // Facing test: compare current yaw against anchor's sweet spot.
              // Wrap to [-PI, PI] for cleanest diff.
              let diff = currentYaw - def.facingYaw;
              diff = Math.atan2(Math.sin(diff), Math.cos(diff));
              const facing = Math.abs(diff) < 0.5; // within ~29deg
              return {
                id: def.id,
                label: def.label,
                value: def.value,
                x: sx,
                y: sy,
                side: def.side,
                onScreen: inFrustum && facing,
              };
            });
            pushAnchors(out);
          } else {
            pushAnchors([]);
          }

          animationId = requestAnimationFrame(animate);
        };
        animate();
      } catch (e) {
        console.error('Three.js init failed', e);
      }
    })();

    return () => {
      disposed = true;
      if (animationId) cancelAnimationFrame(animationId);
      if (unsubScroll) unsubScroll();
      if (handleResize) window.removeEventListener('resize', handleResize);
      if (handlePointerMove)
        container.removeEventListener('pointermove', handlePointerMove);
      if (handlePointerLeave)
        container.removeEventListener('pointerleave', handlePointerLeave);
      if (renderer) {
        renderer.dispose?.();
        if (renderer.domElement && renderer.domElement.parentNode === container) {
          container.removeChild(renderer.domElement);
        }
      }
      if (scene) {
        scene.traverse((obj: any) => {
          if (obj.isMesh) {
            obj.geometry?.dispose?.();
            if (obj.material) {
              if (Array.isArray(obj.material)) {
                obj.material.forEach((m: any) => m.dispose?.());
              } else {
                obj.material.dispose?.();
              }
            }
          }
        });
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 w-full h-full"
      style={{ zIndex: 10 }}
    />
  );
}

void smoothstep01;